import { cn } from "utils";

/** Matches `*.schema.json` asset `type` enum. */
export type JsonRenderSchemaAssetType = "stock" | "crypto";

export interface AssetRecommendationListItem {
  id?: string;
  assetLogo?: string;
  assetSymbol: string;
  assetPriceUsd: number;
  priceChange24h: number;
  reason: string;
  assetScoreValue: number;
  assetScoreTotal: number;
  type: JsonRenderSchemaAssetType;
}

export interface AssetDimensionTableRow {
  dimensionName: string;
  scoreValue: number;
  scoreTotal: number;
  rankValue: number;
  rankTotalAssets: number;
}

export interface DimensionDefinition {
  dimensionKey: string;
  dimensionName: string;
}

export interface MultiAssetDimensionItem {
  id?: string;
  assetLogo?: string;
  assetSymbol: string;
  assetName: string;
  dimensionScores: DimensionScore[];
  type: JsonRenderSchemaAssetType;
}

interface DimensionScore {
  dimensionKey: string;
  scoreValue: number;
  scoreTotal: number;
  rankValue: number;
  rankTotalAssets: number;
}

export interface SummaryMetric {
  metricKey: string;
  metricName: string;
  metricValue: string;
  metricChange: string;
}

export interface TableColumn {
  columnKey: string;
  columnTitle: string;
  columnStyle?: "default" | "badge" | "accent";
}

interface TableCell {
  columnKey: string;
  cellValue: string;
}

export interface TableRow {
  rowKey: string;
  rowName: string;
  cells: TableCell[];
}

export interface Highlight {
  title: string;
  description: string;
}

export interface DimensionRankChange {
  dimensionKey: string;
  dimensionName: string;
  rankValue: number;
  rankTotalAssets: number;
  rankChange: number;
}

export interface RatingChange {
  id?: string;
  assetLogo?: string;
  assetSymbol: string;
  fromRating: string;
  toRating: string;
  type: JsonRenderSchemaAssetType;
}

/** Tone matches `NotificationButtonList.schema.json` → `actions[].type`. */
type NotificationButtonTone = "negative" | "positive";

/**
 * One footer button; business fields live in `attributes` (see component-json-schemas).
 * `type`: negative = secondary-style feedback; positive = primary CTA.
 */
export interface NotificationAction {
  type: NotificationButtonTone;
  attributes: Record<string, unknown>;
}

export const askEdButtonClass = cn(
  "inline-flex min-w-[80px] items-center justify-center gap-1 rounded-[6px] border-0 px-2 py-1.5",
  "bg-background_brand_overlay100 text-content_inverse_primary",
  "hover:brightness-95 active:brightness-90",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background_brand_overlay100/60",
);

/** Figma 42577:3848 / 42577:3915 — Tracker View Details */
export const trackerViewDetailButtonClass = cn(
  "inline-flex h-9 w-full min-w-[80px] items-center justify-center gap-0.5 rounded-2 border-0 px-3 py-2",
  "bg-background_brand_overlay100",
  "hover:brightness-95 active:brightness-90",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background_brand_overlay100/60",
);

/** Figma 42577:3849 / 42577:3916 — Tracker Ask Ed：透明底 + 左侧 p_chat + content/secondary 文案 */
export const trackerAskEdButtonClass = cn(
  "inline-flex h-9 shrink-0 items-center justify-start gap-0.5 rounded-2 border-0 bg-transparent px-3 py-2",
  "hover:bg-background_white_overlay5 active:bg-background_white_overlay10",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border_white_transparent20",
);

/** Figma 42577:3850 / 42577:3917 — Tracker dislike 圆钮 */
export const trackerDislikeButtonClass = cn(
  "inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-border_white_transparent10 bg-background_solid_neutral_primary",
);

/** Figma 42577:3847 / 42577:3914 — Tracker Actions 行（329 = 178 + 12 + 91 + 12 + 36） */
export const trackerActionsRowClass = cn(
  "grid w-full min-h-12 grid-cols-[minmax(80px,1fr)_auto_36px] items-center gap-3 pt-3",
);

/** Figma 94917:42893 — Invest Moves Talk to Ed CTA */
export const investMovesAskEdButtonClass = cn(
  "inline-flex min-w-[80px] items-center justify-center gap-1 rounded-[6px] px-2 py-1.5",
  "bg-background_brand_overlay10 text-content_brand_primary",
  "hover:brightness-95 active:brightness-90",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background_brand_overlay10/60",
);

/** Figma 94917:42892 — Invest Moves footer row (CTA + relative time) */
export const investMovesActionsRowClass = cn(
  "flex w-full items-center justify-between gap-3",
);

export const notificationNormalClass = cn(
  "rounded-1.5 border-border_white_transparent20 border px-2 py-1.5 items-center inline-flex justify-center gap-1",
);

/** Scene-specific display overrides (client enrich); avoids scattering business fields on props. */
export interface NotificationCardSceneContext {
  /** Invest moves: `trackers[tracker_id].title` from enrich_tracker */
  trackerTitle?: string;
  /** Invest moves: record.goal_id — drives label icon (goal vs signal) */
  goalId?: string;
}

/** FF §3.2.4 — Sharpen Chat card types registered for JsonRenderComponents */
export type SharpenCardType =
  "SHARPEN_CONFIRM" | "SHARPEN_RESULT" | "SHARPEN_NEXT_STEP";
